import { createFileRoute, Link } from "@tanstack/react-router";
import { Clock, Loader2, LogOut, Swords } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import type { LeaderboardEntry } from "../../../../../../components/leaderboard";
import { LeaderboardTable } from "../../../../../../components/leaderboard";
import { Breadcrumbs, Button } from "../../../../../../components/ui";
import { useSession } from "../../../../../../lib/auth-client";
import { trpc } from "../../../../../../lib/trpc";

export const Route = createFileRoute("/app/clubs/$slug/queues/$queueSlug/")({
  component: QueueDetailPage,
});

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function QueueDetailPage() {
  const { slug, queueSlug } = Route.useParams();
  const { data: session } = useSession();
  const userId = session?.user?.id;

  const { data: club } = trpc.club.get.useQuery({ slug });
  const clubId = club?.id;

  const { data: myClubs } = trpc.club.getMyClubs.useQuery();
  const membership = myClubs?.find((c) => c.slug === slug);
  const isMember = !!membership;

  const { data: queue, isLoading: queueLoading } = trpc.queue.get.useQuery({
    clubSlug: slug,
    queueSlug,
  });

  const queueId = queue?.id ?? "";

  const { data: games } = trpc.clubGame.list.useQuery(
    { clubId: clubId ?? "" },
    { enabled: !!clubId },
  );
  const gameName = useMemo(() => {
    if (!games || !queue) return null;
    const game = games.find((g) => g.id === queue.clubGameId);
    return game?.name ?? null;
  }, [games, queue]);

  const [leaderboardCursor, setLeaderboardCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  const { data: leaderboardData } = trpc.leaderboard.get.useQuery(
    { clubId: clubId ?? "", queueId, limit: 100, cursor: leaderboardCursor },
    { enabled: !!clubId && !!queueId, placeholderData: (prev) => prev },
  );

  const handleNextPage = () => {
    if (leaderboardData?.nextCursor) {
      setCursorHistory((prev) => [...prev, leaderboardCursor ?? ""]);
      setLeaderboardCursor(leaderboardData.nextCursor);
    }
  };

  const handlePrevPage = () => {
    setCursorHistory((prev) => {
      const next = [...prev];
      const prevCursor = next.pop();
      setLeaderboardCursor(prevCursor || undefined);
      return next;
    });
  };

  const currentPage = cursorHistory.length + 1;

  // Fetch current user's rank/stats for pinned row (members only)
  const { data: myRank } = trpc.leaderboard.getPlayerRank.useQuery(
    { clubId: clubId ?? "", queueId, userId: userId ?? "" },
    { enabled: isMember && !!clubId && !!queueId && !!userId },
  );

  const utils = trpc.useUtils();
  const { data: queueStatus } = trpc.queueEntry.batchStatus.useQuery(
    { queueIds: [queueId] },
    { enabled: isMember && !!session?.user && !!queueId },
  );
  const isInQueue = queueStatus?.[queueId]?.isInQueue ?? false;

  const joinMutation = trpc.queueEntry.join.useMutation({
    onSuccess: () => {
      toast.success(`Joined ${queue?.name ?? "queue"}`);
      void utils.queueEntry.batchStatus.invalidate();
      if ("Notification" in window && Notification.permission === "default") {
        void Notification.requestPermission();
      }
    },
    onError: (err) => {
      if (err.data?.code === "CONFLICT") {
        toast.error("Already in this queue");
      } else {
        toast.error(err.message || "Failed to join queue");
      }
    },
  });

  const leaveMutation = trpc.queueEntry.leave.useMutation({
    onSuccess: () => {
      toast.success(`Left ${queue?.name ?? "queue"}`);
      void utils.queueEntry.batchStatus.invalidate();
    },
    onError: () => toast.error("Failed to leave queue"),
  });

  const isQueuePending = joinMutation.isPending || leaveMutation.isPending;

  if (queueLoading || !clubId) {
    return <QueueDetailSkeleton />;
  }

  if (!queue) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
          <h2 className="text-xl font-bold text-white mb-2">Queue not found</h2>
          <p className="text-sm text-surface-400">This queue may have been removed.</p>
          <Link to="/app/clubs/$slug" params={{ slug }} className="mt-4">
            <Button variant="outline" size="md">
              Back to Overview
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const teamSize =
    queue.teamSize ??
    (queue.matchSize && queue.teamCount ? Math.floor(queue.matchSize / queue.teamCount) : null);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 pb-8 md:pb-10 space-y-3">
      <div className="flex items-center">
        <Breadcrumbs
          items={[
            {
              label: club?.name ?? slug,
              to: "/app/clubs/$slug",
              params: { slug },
            },
            { label: queue.name },
          ]}
        />
      </div>

      {/* Banner + control strip */}
      <div className="max-w-[768px] w-full mx-auto">
        <div className="relative aspect-[4/1] rounded-xl overflow-hidden">
          {queue.bannerUrl ? (
            <img
              src={queue.bannerUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute inset-0"
                style={{
                  background: `
                    radial-gradient(ellipse 70% 200% at 70% 30%, rgba(38,217,181,0.2) 0%, transparent 60%),
                    linear-gradient(135deg, #0d1f1b 0%, #101318 45%, #111a1f 100%)
                  `,
                }}
              />
              <div
                className="absolute top-[-60%] right-[5%] w-[45%] h-[280%] bg-gradient-to-b from-brand-500/[0.12] via-brand-500/[0.06] to-transparent"
                style={{ transform: "rotate(-20deg)" }}
              />
              <div
                className="absolute top-[-40%] right-[22%] w-[8%] h-[250%] bg-gradient-to-b from-brand-400/[0.1] to-transparent"
                style={{ transform: "rotate(-20deg)" }}
              />
              <div
                className="absolute top-[-30%] left-[8%] w-[20%] h-[220%] bg-gradient-to-b from-cyan-400/[0.06] to-transparent"
                style={{ transform: "rotate(-20deg)" }}
              />
              <div className="absolute bottom-0 inset-x-0 h-[40%] bg-gradient-to-t from-brand-500/[0.08] to-transparent" />
              <div className="absolute inset-0 noise-overlay" />
            </div>
          )}
          <div className="absolute inset-0 noise-overlay" />

          {/* Game + team badges — top left */}
          <div className="absolute top-3 left-4 flex items-center gap-1.5 z-10">
            {gameName && (
              <span className="px-2 py-1 rounded bg-surface-900/60 backdrop-blur-md border border-surface-700/40 text-[11px] font-semibold text-surface-300">
                {gameName}
              </span>
            )}
            {teamSize && (
              <span className="px-2 py-1 rounded bg-surface-900/60 backdrop-blur-md border border-surface-700/40 text-[11px] font-semibold text-surface-300 tabular-nums">
                {teamSize}v{teamSize}
              </span>
            )}
          </div>

          {/* In queue pill — top right */}
          <div className="absolute top-3 right-4 z-10">
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-surface-900/60 backdrop-blur-md border border-surface-700/40">
              <span className="relative flex size-1.5">
                <span className="absolute inset-0 rounded-full bg-brand-400 animate-ping opacity-50" />
                <span className="relative rounded-full size-1.5 bg-brand-400" />
              </span>
              <span className="text-[11px] font-display font-bold text-brand-300 tabular-nums uppercase">
                {queue.playersInQueue ?? 0} in queue
              </span>
            </div>
          </div>

          {/* Bottom gradient with icon, name, and actions */}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-12 pb-3 px-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                {queue.iconUrl ? (
                  <img
                    src={queue.iconUrl}
                    alt=""
                    className="size-8 rounded-lg object-cover shrink-0 shadow-[0_2px_8px_rgba(0,0,0,0.5)]"
                  />
                ) : (
                  <div className="size-8 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center shrink-0">
                    <Swords className="size-4 text-white/70" />
                  </div>
                )}
                <h1 className="text-sm sm:text-lg font-display font-bold text-white truncate">
                  {queue.name}
                </h1>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                {isMember &&
                  (isInQueue ? (
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={isQueuePending}
                      leftIcon={
                        isQueuePending ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <LogOut className="size-3.5" />
                        )
                      }
                      onClick={() => leaveMutation.mutate({ queueId })}
                    >
                      Leave
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={isQueuePending}
                      leftIcon={
                        isQueuePending ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <Swords className="size-3.5" />
                        )
                      }
                      onClick={() => joinMutation.mutate({ queueId })}
                    >
                      Join Queue
                    </Button>
                  ))}
                <Link
                  to="/app/clubs/$slug/matches"
                  params={{ slug }}
                  search={{
                    clubGameId: undefined,
                    queueId,
                    userId: undefined,
                    me: true,
                    period: undefined,
                  }}
                >
                  <Button variant="secondary" size="sm" leftIcon={<Clock className="size-3.5" />}>
                    <span className="hidden sm:inline">Match History</span>
                    <span className="sm:hidden">History</span>
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full-Width Leaderboard */}
      <LeaderboardTable
        items={leaderboardData?.items as LeaderboardEntry[] | undefined}
        totalCount={leaderboardData?.totalCount ?? 0}
        currentPage={currentPage}
        hasNextPage={!!leaderboardData?.nextCursor}
        hasPrevPage={cursorHistory.length > 0}
        onNextPage={handleNextPage}
        onPrevPage={handlePrevPage}
        currentUserId={userId}
        currentUserEntry={myRank as LeaderboardEntry | null | undefined}
        clubId={clubId}
        queueId={queueId}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function QueueDetailSkeleton() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="size-8 text-surface-500 animate-spin" />
    </div>
  );
}
